/**
 * Input unificato: tastiera (WASD/frecce) e joystick virtuale touch
 * convergono in un unico asse analogico + azioni discrete.
 * I consumer (sistemi di gioco) non sanno da quale device arriva l'input.
 */

export type GameAction = 'interact' | 'menu';

export class InputManager {
  /** Asse di movimento normalizzato, |axis| <= 1. */
  axis = { x: 0, y: 0 };

  private keys = new Set<string>();
  private touchAxis = { x: 0, y: 0 };
  private actionHandlers = new Map<GameAction, Set<() => void>>();
  private detachFns: (() => void)[] = [];

  attach(target: Window = window): void {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      this.keys.add(e.code);
      if (e.code === 'KeyE' || e.code === 'Enter' || e.code === 'Space') {
        this.fire('interact');
      }
      if (e.code === 'Escape' || e.code === 'KeyM') {
        this.fire('menu');
      }
    };
    const onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.code);
    const onBlur = () => this.keys.clear();
    target.addEventListener('keydown', onKeyDown);
    target.addEventListener('keyup', onKeyUp);
    target.addEventListener('blur', onBlur);
    this.detachFns.push(() => {
      target.removeEventListener('keydown', onKeyDown);
      target.removeEventListener('keyup', onKeyUp);
      target.removeEventListener('blur', onBlur);
    });
  }

  detach(): void {
    for (const fn of this.detachFns) fn();
    this.detachFns = [];
  }

  /** Da chiamare una volta per frame logico. */
  update(): void {
    let x = 0;
    let y = 0;
    if (this.keys.has('ArrowLeft') || this.keys.has('KeyA')) x -= 1;
    if (this.keys.has('ArrowRight') || this.keys.has('KeyD')) x += 1;
    if (this.keys.has('ArrowUp') || this.keys.has('KeyW')) y -= 1;
    if (this.keys.has('ArrowDown') || this.keys.has('KeyS')) y += 1;

    // il joystick touch ha priorità se attivo
    if (Math.abs(this.touchAxis.x) > 0.01 || Math.abs(this.touchAxis.y) > 0.01) {
      x = this.touchAxis.x;
      y = this.touchAxis.y;
    }

    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    this.axis.x = x;
    this.axis.y = y;
  }

  setTouchAxis(x: number, y: number): void {
    this.touchAxis.x = x;
    this.touchAxis.y = y;
  }

  on(action: GameAction, handler: () => void): () => void {
    let set = this.actionHandlers.get(action);
    if (!set) {
      set = new Set();
      this.actionHandlers.set(action, set);
    }
    set.add(handler);
    return () => set.delete(handler);
  }

  fire(action: GameAction): void {
    const set = this.actionHandlers.get(action);
    if (!set) return;
    for (const handler of [...set]) handler();
  }
}
